import React, { useCallback, useEffect, useRef } from 'react';
import { Check, UserRound } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import type { PublicAgent } from '../../services/agentService';
import { useAgentImage } from '../../hooks/useAgentImage';

/**
 * CANDIDATE BENCH — the faces waiting to be auditioned, in one row under the audition.
 *
 * It replaces a second pair of chevrons that stepped blindly from candidate to candidate (owner 2026-08-23:
 * "the arrow to switch agent is also not obvious" — it looked identical to the arrows that page the PHOTOS of
 * one candidate). Two axes that look the same are one axis too many, so the person-axis stops being arrows and
 * becomes what a hiring room actually has: everyone who could take the seat, visible at once, picked in ONE
 * click instead of N. The photo-axis stays where it belongs, on the picture.
 *
 * A tablist, not a list of buttons: picking a face swaps the audition panel, which is exactly what a tab does —
 * and it buys the keyboard behaviour for free (one tab stop, arrows walk the bench, Home/End jump the ends).
 */
export const AUDITION_PANEL_ID = 'hiring-audition-panel';

const BenchFace: React.FC<{
  agent: PublicAgent;
  selected: boolean;
  /** Already holds the seat — the bench says so, so a browse never loses "who do I have now". */
  incumbent: boolean;
  position: string;
  onPick: () => void;
  registerRef: (el: HTMLButtonElement | null) => void;
}> = ({ agent, selected, incumbent, position, onPick, registerRef }) => {
  const src = useAgentImage(agent.avatar_image);
  return (
    <button
      type="button"
      ref={registerRef}
      role="tab"
      aria-selected={selected}
      aria-controls={AUDITION_PANEL_ID}
      tabIndex={selected ? 0 : -1}
      onClick={onPick}
      aria-label={`${agent.name}, candidate ${position}${incumbent ? ', currently in the seat' : ''}`}
      className="group flex w-14 shrink-0 flex-col items-center gap-1 rounded-lg p-1 focus:outline-none
                 focus-visible:ring-2 focus-visible:ring-secondary-900 dark:focus-visible:ring-white"
    >
      <span
        className={`relative block h-14 w-11 overflow-hidden rounded-md bg-secondary-100 transition-opacity dark:bg-secondary-800
                    ${selected
                      ? 'opacity-100 ring-2 ring-secondary-900 ring-offset-2 ring-offset-white dark:ring-white dark:ring-offset-secondary-900'
                      : 'opacity-60 group-hover:opacity-100'}`}
      >
        {src
          ? <img src={src} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
          : <UserRound className="absolute inset-0 m-auto h-4 w-4 text-secondary-400" aria-hidden />}
        {incumbent && (
          <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-bl-md bg-secondary-900 text-white dark:bg-white dark:text-secondary-900">
            <Check className="h-2.5 w-2.5" aria-hidden />
          </span>
        )}
      </span>
      <span className={`${TYPE.micro} w-full truncate text-center ${selected
        ? 'font-semibold text-secondary-900 dark:text-white'
        : 'text-secondary-500 dark:text-secondary-400'}`}>
        {agent.name}
      </span>
    </button>
  );
};

const CandidateBench: React.FC<{
  candidates: PublicAgent[];
  index: number;
  onPick: (index: number) => void;
  /** Who holds the seat right now, if anyone. */
  currentId?: string | null;
}> = ({ candidates, index, onPick, currentId }) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // Keep the auditioned face in view when the bench is picked through by keyboard (or opens deep in a long
  // bench). Honour the reduced-motion preference — a scroll animation is motion like any other.
  useEffect(() => {
    const el = refs.current[index];
    if (!el) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    el.scrollIntoView?.({ behavior: still ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  }, [index]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const n = candidates.length;
    const next = e.key === 'ArrowRight' ? (index + 1) % n
      : e.key === 'ArrowLeft' ? (index - 1 + n) % n
        : e.key === 'Home' ? 0
          : e.key === 'End' ? n - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    onPick(next);
    refs.current[next]?.focus();
  }, [candidates.length, index, onPick]);

  if (candidates.length < 2) return null;

  return (
    /* Sticky to the foot of the hiring room: on a short screen the audition scrolls, but the people you
       could pick stay in front of you — a bench you have to go looking for is the old blind stepper again. */
    <div className="sticky bottom-0 -mb-4 border-t border-secondary-200 bg-white pb-4 pt-3 dark:border-secondary-800 dark:bg-secondary-900">
      <div className="flex items-baseline justify-between">
        <p className={`${TYPE.label} text-secondary-400 dark:text-secondary-500`}>Candidates</p>
        <p className={`${TYPE.meta} text-secondary-400`}>{index + 1} of {candidates.length}</p>
      </div>
      {/* Everyone who could take the seat. Scrolls sideways when the bench is longer than the room. */}
      <div
        role="tablist"
        aria-label="Candidates for this seat"
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className="mt-1.5 flex gap-1 overflow-x-auto pb-1"
      >
        {candidates.map((a, i) => (
          <BenchFace
            key={a.id}
            agent={a}
            selected={i === index}
            incumbent={!!currentId && a.id === currentId}
            position={`${i + 1} of ${candidates.length}`}
            onPick={() => onPick(i)}
            registerRef={(el) => { refs.current[i] = el; }}
          />
        ))}
      </div>
    </div>
  );
};

export default CandidateBench;
